@if (isLoading) {
  <section
    class="animate-pulse"
    role="status"
    aria-label="Loading ticket detail"
  >
    <div class="flex w-full items-center bg-white p-4 md:hidden">
      <div class="h-4 w-28 rounded bg-gray-200"></div>
    </div>

    <div class="bg-white px-4 py-4">
      <div
        class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="flex items-center gap-3">
          <div class="h-4 w-20 rounded bg-gray-200"></div>
          <div class="h-6 w-14 rounded-md bg-gray-200"></div>
        </div>
        <div class="h-4 w-40 rounded bg-gray-200"></div>
      </div>
      <div class="mt-4 h-7 w-48 rounded bg-gray-200"></div>
    </div>

    <div class="basis-full space-y-6 p-4">
      <div class="space-y-4 lg:space-y-3">
        @for (row of [0, 1, 2]; track row) {
          <div
            class="grid grid-cols-1 gap-y-2 lg:grid-cols-[88px_1fr] lg:gap-x-4"
          >
            <div class="h-3 w-24 rounded bg-gray-200 lg:w-20"></div>
            <div
              class="h-4 rounded bg-gray-200"
              [class.w-10/12]="row === 0"
              [class.w-8/12]="row === 1"
              [class.w-6/12]="row === 2"
            ></div>
          </div>
        }
      </div>

      <div>
        <div class="h-3 w-32 rounded bg-gray-200"></div>
        <div class="mt-3 flex items-start gap-3">
          <div class="h-10 w-10 shrink-0 rounded-full bg-gray-200"></div>
          <div class="min-w-0 flex-1 space-y-2">
            <div class="h-5 w-36 rounded bg-gray-200"></div>
            <div class="h-4 w-64 max-w-full rounded bg-gray-200"></div>
          </div>
        </div>
      </div>

      <div>
        <div class="h-3 w-14 rounded bg-gray-200"></div>
        <div
          class="mt-3 rounded-lg border border-dashed border-gray-200 bg-white px-3 py-4 lg:px-4"
        >
          <div class="flex items-start gap-2 lg:gap-3">
            <div class="h-6 w-6 shrink-0 rounded bg-gray-200"></div>
            <div class="min-w-0 flex-1 space-y-2">
              <div class="h-4 w-28 rounded bg-gray-200"></div>
              <div class="h-3 w-10/12 rounded bg-gray-200"></div>
              <div class="h-3 w-7/12 rounded bg-gray-200"></div>
            </div>
          </div>
        </div>
        <div class="mt-2 h-3 w-11/12 rounded bg-gray-200"></div>
      </div>

      <div>
        <div class="h-3 w-28 rounded bg-gray-200"></div>
        <div class="mt-3 flex gap-2 lg:gap-3">
          <div
            class="hidden h-8 w-8 shrink-0 rounded-full bg-gray-200 lg:block"
          ></div>
          <div class="min-w-0 flex-1 space-y-2">
            <div class="h-24 rounded border border-gray-200 bg-white"></div>
            <div
              class="ml-auto h-10 w-full rounded bg-gray-200 lg:h-8 lg:w-20"
            ></div>
          </div>
        </div>
        <div class="mt-6 flex items-start gap-2 lg:gap-3">
          <div
            class="h-7 w-7 shrink-0 rounded-full bg-gray-200 lg:h-8 lg:w-8"
          ></div>
          <div class="min-w-0 flex-1 space-y-2">
            <div class="h-4 w-32 rounded bg-gray-200"></div>
            <div class="h-12 rounded-lg bg-gray-200"></div>
          </div>
        </div>
      </div>
    </div>

    <span class="sr-only">Loading ticket detail…</span>
  </section>
} @else {
  @if (ticket$ | async; as ticket) {
    <section>
      <div class="flex w-full items-center bg-white text-base md:hidden">
        <a
          class="font-lato flex min-h-12 cursor-pointer items-center p-4 text-xs font-bold text-blue-700 uppercase"
          [routerLink]="['/tickets']"
        >
          <mat-icon class="mr-2 text-sm">arrow_back</mat-icon>
          Back to Tickets
        </a>
      </div>
      <div class="basis-full px-4 pt-3 pb-4">
        <div #ticketDetailContent>
          <!-- Case Number, Title, Date Created/Closed -->
          <app-ticket-detail-header
            [ticket]="ticket"
            #ticketDetailHeader
          ></app-ticket-detail-header>
        </div>
        <div id="ticketDetailScroll">
          <!-- Slim details toggle — mobile only -->
          <button
            type="button"
            class="mb-3 flex w-full items-center gap-2 py-3 focus:outline-none md:hidden"
            (click)="showDetails.set(!showDetails())"
            [attr.aria-expanded]="showDetails()"
            aria-controls="ticket-supporting-details"
          >
            <span class="h-px flex-1 bg-gray-300"></span>
            <span class="font-lato text-sm font-semibold text-gray-400">
              {{ showDetails() ? 'Hide details' : 'View details' }}
            </span>
            <mat-icon
              class="!flex !h-4 !w-4 !items-center !justify-center !text-base text-gray-400 transition-transform duration-200"
              [class.rotate-180]="showDetails()"
              aria-hidden="true"
              >expand_more</mat-icon
            >
            <span class="h-px flex-1 bg-gray-300"></span>
          </button>

          <div
            id="ticket-supporting-details"
            #ticketDetailContent
            class="md:block"
            [class.hidden]="!showDetails()"
          >
            <!-- Locaion & Service Info -->
            <app-ticket-detail-service
              [ticket]="ticket"
            ></app-ticket-detail-service>

            <!-- Primary Reporter -->
            @if (ticket.Contact?.Name) {
              <app-ticket-detail-contact
                [ticket]="ticket"
              ></app-ticket-detail-contact>
            }

            @if (isNocTicket()) {
              <!-- Desktop only: inside collapsible details -->
              <app-ticket-iq-observation
                [ticket]="ticket"
                class="mt-4 hidden md:block"
              ></app-ticket-iq-observation>
            }

            @if (!ticket.IsClosed) {
              <!-- File Uploads -->
              <div class="mt-3">
                <span
                  class="font-lato text-xs font-semibold tracking-[0.06em] text-gray-400 uppercase"
                  >Files</span
                >
                <app-file-upload
                  [ticket]="ticket"
                  [allowUserInput]="allowUserInput()"
                  (filesUploaded)="fileUploaded($event)"
                ></app-file-upload>
              </div>
            }

            <!-- Comments Header (for large screens only) -->
            <div class="mt-6 hidden md:block">
              <span
                class="font-lato text-xs font-semibold tracking-[0.06em] text-gray-400 uppercase"
              >
                Comments ({{
                  ticket.Comments__r ? ticket.Comments__r.totalSize : '0'
                }})
              </span>
            </div>
          </div>

          <!-- IQ Observation: mobile only, always visible outside collapsible -->
          @if (isNocTicket()) {
            <app-ticket-iq-observation
              [ticket]="ticket"
              class="mt-4 block md:hidden"
            ></app-ticket-iq-observation>
          }

          <!-- Comments label: mobile only (desktop version is inside collapsible) -->
          <div class="mt-4 md:hidden">
            <span
              class="font-lato text-xs font-semibold tracking-[0.06em] text-gray-400 uppercase"
            >
              Comments ({{ ticket.Comments__r?.totalSize || 0 }})
            </span>
          </div>

          <!-- Comments -->
          <app-ticket-comment
            [ticket]="ticket"
            [fileUploadResult]="fileUploadResult()"
            (saved)="reloadTicketsData()"
          ></app-ticket-comment>
        </div>
      </div>
    </section>
  } @else {
    <section
      class="flex h-full flex-col items-center justify-center px-6 py-20 text-center"
    >
      <div
        class="mb-4 flex h-14 w-14 items-center justify-center"
        aria-hidden="true"
      >
        <mat-icon
          fontSet="material-icons-outlined"
          class="!flex !h-12 !w-12 !items-center !justify-center !text-4xl text-black"
          >receipt_long</mat-icon
        >
      </div>
      <p class="font-montserrat text-lg font-semibold">No ticket selected</p>
      <p
        class="font-lato mt-1 max-w-[200px] text-sm leading-relaxed text-gray-400"
      >
        Select a ticket from the list to view its details.
      </p>
    </section>
  }
}
